/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : useComboBox.tsx
// 설명 : 콤보박스 관련 커스텀 훅
//  - 공통 및 사용자정의 콤보박스 데이터를 가져옵니다.
//  .커스텀 훅: 함수명 반드시 use로 시작 (예: useComboBox)
// 	.공통 : useComboBox(공통)
// 	.사용자정의 : useComboBoxSite(사용자정의)
// 최종수정일 : 2026-09-15
//  - 2026-09-15 : 훅을 호출할 때마다(예: useComboBox('cbYN', ...)) React가 매번 독립된 useState를
//    만들어서, 여러 콤보를 하나의 캐시에서 key로 구분해 관리한다는 comboMap(딕셔너리) 설계가 실제로는
//    전혀 쓰이지 않고 있었음(호출 하나당 항상 값이 하나뿐이라 comboMap[key]는 그냥 유일한 값을 꺼내는
//    것과 동일). key 파라미터를 완전히 제거하고 options 하나만 관리하도록 단순화함.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useEffect, useState, useMemo} from "react";
import React, { useImperativeHandle, forwardRef } from "react";
import axios from "axios";

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 사용자정의 컴포넌트 import
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { API_BASE_URL } from '../config'; // API 기본 URL

// 3.1 공통콤보 : 정의
export interface ComboBoxOption {
  value: string;
  label: string;
}

/** params 정의
 * @param hcode : 콤보코드
 * @param includeAll : 전체포함여부
*/
export const useComboBox = (
  hcode: string,
  includeAll: boolean = true,
) => {
  const [options, setOptions] = useState<ComboBoxOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hcode) return;
    setLoading(true);
    axios
      .post(`${API_BASE_URL}/com/apiComboBox`, { hcode })
      .then(res => {
        if (Array.isArray(res.data.rsResults)) {
          setOptions(includeAll
            ? [{ value: '%', label: '전체' }, ...res.data.rsResults]
            : res.data.rsResults);
        }
      })
      .catch(() => {
        // 실패 시 기존 값 유지
      })
      .finally(() => setLoading(false));
  }, [hcode, includeAll]);

  // 반환: 콤보박스 옵션과 로딩 상태
  return { options };
};

/**
 * 사업부 콤보박스 조회
 * @param pAuthFlg : 권한 플래그
 * @param includeAll: boolean -  전체포함여부
 */
export const useComboBoxBizUnit = (
  pAuthFlg: string,
  includeAll: boolean = true,
) => {
  const [options, setOptions] = useState<ComboBoxOption[]>([]);
  const [loading, setLoading] = useState(false);

  // 사용자 정보 가져오기
  const retUserInfo = localStorage.getItem('userInfo');
  let pUserId = retUserInfo ? JSON.parse(retUserInfo).userId : '',  // 사용자 아이디, 필요시 수정
      pUserName = retUserInfo ? JSON.parse(retUserInfo).userName : '';

  useEffect(() => {
    setLoading(true);
    axios
      .post(`${API_BASE_URL}/com/apiComboBoxBizUnit`, { pUserId, pAuthFlg })
      .then(res => {
        if (Array.isArray(res.data.rsResults)) {
          setOptions(includeAll
            ? [{ value: '%', label: '전체' }, ...res.data.rsResults]
            : res.data.rsResults);
        }
      })
      .catch(() => {
        // 실패 시 기존 값 유지
      })
      .finally(() => setLoading(false));
  }, [pUserId, pAuthFlg, includeAll]);

  // 반환: 콤보박스 옵션과 로딩 상태
  return { options };
};

// 3.2 사용자정의 콤보
interface ComboBoxSiteParams {
  pSelect?: string;
  pTableId?: string;
  pWhere?: string;
  pOrderby?: string;
  pAuthFlg?: string;
}

/** params 정의
 * pSelect?: string - 선택할 필드
 * pTableId?: string - 테이블 ID
 * pWhere?: string - WHERE 조건
 * pOrderby?: string - ORDER BY 조건
 * pAuthFlg?: string - 권한 플래그(기본 Y:권한적용, N:권한미적용)
*/
export const useComboBoxSite = (params: ComboBoxSiteParams, includeAll = true) => {
  const [options, setOptions] = useState<ComboBoxOption[]>([]);
  const [loading, setLoading] = useState(false);

  // params 안정화 (stableParamsKey 내용이 바뀔 때만 새 참조 생성)
  const stableParamsKey = JSON.stringify(params);
  const stableParams = useMemo(() => params, [stableParamsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!stableParams.pTableId || !stableParams.pSelect) return;
    setLoading(true);
    axios
      .post(`${API_BASE_URL}/com/apiComboBoxSite`, { ...stableParams })
      .then(res => {
        if (Array.isArray(res.data.rsResults)) {
          setOptions(includeAll
            ? [{ value: '%', label: '전체' }, ...res.data.rsResults]
            : res.data.rsResults);
        }
      })
      .catch(() => {
        // 실패 시 기존 값 유지
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [includeAll, stableParams]);

  return { options };
};
